import { Link } from "@tanstack/react-router";
import { Button } from "primereact/button";
import { Password } from "primereact/password";
import { useEffect, type FC } from "react";
import { Controller, useForm } from "react-hook-form";
import AuthCard, { submitButtonPt } from "@/components/AuthCard";
import authStyles from "@/components/AuthCard.module.css";
import { ROUTES } from "@/constants/routes";
import { TEXT } from "@/constants/text";
import { isStaffEmail, toLoginHint } from "@/utils/validation";
import EmailField from "@/pages/auth/EmailField";
import styles from "@/pages/auth/LoginPage/LoginForm.module.css";
import {
  landAfterLogin,
  login,
  startLogin,
} from "@/apis/clients/sessionClient";

interface LoginFormValues {
  email: string;
  password: string;
}

const passwordPt = {
  input: { className: authStyles.input },
};

const LoginForm: FC = () => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>();

  const email = watch("email") ?? "";

  // Staff go straight to Auth0, which collects the password itself, so the
  // field is hidden for them — and unregistered with it, or react-hook-form
  // would still require a value nothing can consume and block the form.
  const needsPassword = !isStaffEmail(email);

  // A rejected credential describes one submitted pair, so editing the address
  // makes it stale. react-hook-form clears root errors only on the next submit,
  // which would leave a "wrong password" message attached to an address that
  // was never tried.
  useEffect(() => {
    clearErrors("root");
  }, [email, clearErrors]);

  // Two paths, because there are two kinds of user.
  //
  // Staff are handed to the backend, which redirects to Auth0. A full-page
  // navigation, not a fetch: that endpoint answers 302, and fetch would follow
  // it opaquely without ever moving the browser. The typed email goes along as
  // a login_hint so Auth0 prefills it.
  //
  // Everyone else is an external user, whose credentials this service does
  // handle: the backend mangles them and performs the Auth0 password grant
  // server-to-server. On success the browser holds a session cookie but every
  // in-memory store still describes a logged-out app, so the landing is a full
  // page load rather than a router transition — the same reason `logout` uses
  // one, and what the staff path gets for free from its 302.
  const onSubmit = async (values: LoginFormValues) => {
    if (!needsPassword) {
      startLogin(toLoginHint(values.email));
      return;
    }

    // The request can outlive the address that started it: someone can edit
    // the field while it's in flight. A result is only reported if the typed
    // address still matches what was submitted — a rejection or an error for
    // an address the user has since changed away from describes nothing
    // about what's on screen now. Success still lands regardless: the grant
    // went through and the cookie is set, so the session genuinely exists.
    // Comparing on address alone is only sufficient because at most one
    // request is ever in flight — `disabled={isSubmitting}` on the submit
    // button below guarantees that. Without it, two attempts for the same
    // address could both pass this check, and whichever resolves last would
    // overwrite the other's outcome regardless of which was actually latest.
    const submitted = values.email;
    let accepted: boolean;
    try {
      accepted = await login(submitted, values.password);
    } catch {
      if (getValues("email") !== submitted) return;
      setError("root", { message: TEXT.auth.login.signInUnavailable });
      return;
    }
    if (accepted) {
      landAfterLogin();
      return;
    }
    if (getValues("email") !== submitted) return;
    setError("root", { message: TEXT.auth.login.invalidCredentials });
  };

  return (
    <div>
      <AuthCard
        subtitle={TEXT.auth.login.subtitle}
        title={TEXT.auth.login.title}
      >
        <form
          className={`${authStyles.form} p-fluid`}
          onSubmit={handleSubmit(onSubmit)}
        >
          <EmailField
            error={errors.email}
            invalidMessage={TEXT.auth.login.emailInvalid}
            label={TEXT.auth.login.emailLabel}
            register={register}
            requiredMessage={TEXT.auth.login.emailRequired}
          />
          {needsPassword && (
            <div className={authStyles.field}>
              <label className={authStyles.label} htmlFor="password">
                {TEXT.auth.login.passwordLabel}
              </label>
              <Controller
                control={control}
                name="password"
                render={({ field }) => (
                  <Password
                    feedback={false}
                    inputId="password"
                    inputRef={field.ref}
                    pt={passwordPt}
                    toggleMask
                    value={field.value ?? ""}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  />
                )}
                rules={{ required: TEXT.auth.login.passwordRequired }}
              />
              {errors.password && (
                <span className={authStyles.error} role="alert">
                  {errors.password.message}
                </span>
              )}
            </div>
          )}
          {errors.root && (
            <span className={authStyles.error} role="alert">
              {errors.root.message}
            </span>
          )}
          <div className={styles.forgotRow}>
            <Link to={ROUTES.RESET_PASSWORD}>
              {TEXT.auth.login.forgotPasswordLink}
            </Link>
          </div>
          <Button
            disabled={isSubmitting}
            icon="pi pi-arrow-right"
            iconPos="right"
            label={TEXT.auth.login.submitButton}
            pt={submitButtonPt}
            type="submit"
          />
        </form>
      </AuthCard>
      <p className={authStyles.needAccess}>
        {TEXT.auth.login.needAccessText}{" "}
        <a href="mailto:support@infillion.com">
          {TEXT.auth.login.contactLinkLabel}
        </a>
      </p>
    </div>
  );
};

export default LoginForm;
